'use client';

import { memo } from 'react';
import { AuroraWavesBackground } from './aurora-waves-background';
import { FloatingOrbsBackground } from './floating-orbs-background';
import { WarpStarfieldBackground } from './warp-starfield-background';
import { ParticleMeshBackground } from './particle-mesh-background';
import { CrystalShardsBackground } from './crystal-shards-background';
import { VortexTunnelBackground } from './vortex-tunnel-background';
import { DNAHelixBackground } from './dna-helix-background';
import { HexGrid3DBackground } from './hex-grid-3d-background';
import { GridBackground } from './grid-background';
import { MatrixRainBackground } from './matrix-rain-background';

const BG_COMPONENTS = {
  particles: ParticleMeshBackground,
  aurora:    AuroraWavesBackground,
  starfield: WarpStarfieldBackground,
  orbs:      FloatingOrbsBackground,
  vortex:    VortexTunnelBackground,
  dna:       DNAHelixBackground,
  crystals:  CrystalShardsBackground,
  hexgrid:   HexGrid3DBackground,
  grid:      GridBackground,
  matrix:    MatrixRainBackground,
};

/**
 * Renders the selected background animation as a fixed layer behind page content.
 * Used in Demo1Layout. Memoized — only remounts when bgKey changes.
 */
export const LayoutBackgroundLayer = memo(function LayoutBackgroundLayer({ bgKey }) {
  if (!bgKey || bgKey === 'none') return null;

  const Component = BG_COMPONENTS[bgKey];
  if (!Component) return null;

  // Props per animation — optimized for ambient layout use (lower particle count, slower speed)
  const extraProps = {
    particles: { particleCount: 60, connectDistance: 160, speed: 0.6, interactive: false },
    aurora:    { speed: 0.4, blurAmount: 65 },
    starfield: { starCount: 180, speed: 0.5 },
    orbs:      { orbCount: 10, speed: 0.5, interactive: false },
    vortex:    { ringCount: 18, speed: 0.05 },
    dna:       { helixCount: 3, speed: 0.15 },
    crystals:  { shardCount: 14, speed: 0.4, interactive: false },
    hexgrid:   { cols: 16, speed: 0.6, interactive: false },
    grid:      { beams: { count: 8, speed: 3 } },
    matrix:    { fontSize: 16, speed: 0.45, opacity: 0.65 },
  };

  return (
    <div
      className="absolute inset-0 z-0 pointer-events-none"
      aria-hidden="true"
    >
      <Component
        className="absolute inset-0 size-full"
        variant="auto"
        {...(extraProps[bgKey] ?? {})}
      />
    </div>
  );
});
